import React, { lazy, Suspense, useEffect } from 'react';
import { Route, Switch, useLocation } from 'react-router-dom';
import { Button, Col, Row, Spin } from 'antd';

import Login from './components/Login';
import exoticLogo from './assets/images/Logos/exotic-fishing-logo.png';
import backIcon from './assets/images/common/Icons/exotic-back.svg';
import MainLayout from './containers/Layout';
import HomeLayout from './containers/HomeLayout';

import AdminLoginRouter from './adminLoginRoutes';
import UserLoginRouter from './userLoginRoutes';
import Home from './components/home';
import ErrorPage from './errorPage';
import AppDownloadLinks from './components/app_download_links';
import WidgetLoading from './components/widget/components/WidgetLoading';
import MemberSignUpLayout from './components/membership/components/MemberSignUpLayout';
import SandhurstAnglerDetails from './components/form/SandhurstAnglerDetails';
import { useFlags } from 'flagsmith/react';
import AllBookingPackage from './components/user_bookings/packages/AllBookingPackage';
import PackageDetails from './components/user_bookings/packages/PackageDetails';
import PackageOption from './components/user_bookings/packages/PackageOption';

const FisheryPromotion = lazy(() => import('./components/fishery_promotion'));

const AdminRouter = lazy(() => import('./adminRoutes'));
const EventAdminRouter = lazy(() => import('./eventAdminRoutes'));
const MvpAdminRouter = lazy(() => import('./mvpAdminRoutes'));

const EventSignUp = lazy(() => import('./components/event_signup'));
const EventOneClickRedirect = lazy(
  () => import('./components/AnglingTrust/one_click')
);
const RodLicenceWaiverApproval = lazy(
  () =>
    import(
      './components/AnglingTrust/rdo_compliance_management/components/RodLicenceWaiverApproval'
    )
);
const EventTypeApproval = lazy(
  () =>
    import(
      './components/AnglingTrust/rdo_compliance_management/components/EventTypeApproval'
    )
);
const EventComingSoon = lazy(
  () => import('./components/AnglingTrust/one_click/components/ComingSoon')
);
const EventVolunteerAgreementForm = lazy(
  () =>
    import(
      './components/AnglingTrust/admin/components/team/components/VolunteerAgreementForm'
    )
);
const EventSearch = lazy(() => import('./components/AnglingTrust/search'));
const Brands = lazy(() => import('./components/brands'));
const FisheryProfile = lazy(() => import('./components/fishery_profile'));
const EventProfile = lazy(
  () => import('./components/AnglingTrust/event_profile')
);
const UserBookingsPage = lazy(() => import('./components/user_bookings'));
const UserBookingAccountCreationPage = lazy(
  () =>
    import('./components/user_bookings/components/booking_success/UserBookingAccountCreationPage')
);
const UserAsyncPaymentPage = lazy(() => import('./components/async_payments'));
const SearchView = lazy(() => import('./containers/SearchView'));
const Signup = lazy(() => import('./components/signup'));
const MemberAccountCreation = lazy(
  () => import('./components/membership/components/MemberAccountCreation')
);
const MemberWaitListSignUp = lazy(
  () => import('./components/membership/components/MemberWaitListSignUp')
);
const EmailVerificationPage = lazy(
  () => import('./components/signup/components/EmailVerificationPage')
);
const UserProfile = lazy(() => import('./components/user_profile'));
const UserEmailManagement = lazy(
  () => import('./components/user_profile/components/UserEmailManagementPage')
);
const ContactUs = lazy(() => import('./components/contact_us'));
const ContactUsAnglers = lazy(
  () => import('./components/contact_us/components/anglers')
);
const ContactUsFishery = lazy(
  () => import('./components/contact_us/components/fishery')
);
const Articles = lazy(() => import('./components/articles'));
const ViewArticle = lazy(
  () => import('./components/articles/components/Article')
);

const AboutUs = lazy(() => import('./components/common/AboutUs'));
const ThirdPartyBookingWidget = lazy(() => import('./components/widget'));

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};
const ExoticHeader = () => {
  return (
    <Row className='exotic-header' align='middle'>
      <Col xs={24} sm={24} md={8} className='back-home'>
        <div className=' font-montserrat grey-800 fs-20 txt-underline'>
          <img src={backIcon} alt='back' className='logo mg-right-10' />
          BACK TO HOME
        </div>
      </Col>
      <Col xs={24} sm={24} md={8} className='logo-container'>
        <img src={exoticLogo} alt='Exotic Fishing Thailand' className='logo' />
      </Col>
      <Col xs={24} sm={24} md={6} className='partner-logo'>
        <div className='justify-end display-flex-column'>
          <div className='fs-10 gBold sb-blue-800'>Powered By:</div>
          <div className='fs-20 gSemiBold lh-1'>
            <span className='sb-blue-500'>swim</span>booker
          </div>
        </div>
      </Col>
    </Row>
  );
};

const TransparentBodyBackground = () => {
  useEffect(() => {
    if (!document?.body) {
      console.log('No document.body');
      return;
    }
    console.log(document.body);
    document.body.style.backgroundColor = 'transparent';
    return () => {
      if (document?.body) document.body.style.backgroundColor = '#fff';
    };
  }, []);

  return null;
};

const HideChatBot = () => {
  const { pathname } = useLocation();
  const chatBotWidget = document?.getElementById('brevo-conversations');

  useEffect(() => {
    if (!chatBotWidget) {
      return;
    }
    chatBotWidget.style.display = 'none';
    return () => {
      if (chatBotWidget) {
        chatBotWidget.style.display = 'block';
      }
    };
  }, [chatBotWidget, pathname]);

  return null;
};

const BaseRouter = () => {
  const {
    angling_trust_events: { enabled: is_at_events_enabled },
  } = useFlags(['angling_trust_events']);
  return (
    <Switch>
      <Route
        path={['/admin/login', '/event_admin/login', '/sb/admin/login']}
        exact
      >
        <AdminLoginRouter />
      </Route>

      <Route path={['/user/reset/:id', '/user/forgot_password']}>
        <UserLoginRouter />
      </Route>

      <Route path={['/external/:fishery_id']}>
        <HideChatBot />
        <TransparentBodyBackground />
        <ScrollToTop />
        <Suspense fallback={<WidgetLoading />}>
          <Switch>
            <Route
              exact
              path='/external/:fishery_id'
              component={ThirdPartyBookingWidget}
            />
          </Switch>
        </Suspense>
      </Route>

      <Route path={['/admin/v1/:path?/:path?/:path?/:path?']} exact>
        <HideChatBot />
        <Suspense
          fallback={
            <div className='loadingContainer'>
              <Spin tip='Loading...' />
            </div>
          }
        >
          <AdminRouter />
        </Suspense>
      </Route>
      {is_at_events_enabled && (
        <Route path={['/event_admin/:path?/:path?/:path?/:path?']} exact>
          <HideChatBot />
          <Suspense
            fallback={
              <div className='loadingContainer'>
                <Spin tip='Loading...' />
              </div>
            }
          >
            <EventAdminRouter />
          </Suspense>
        </Route>
      )}
      <Route path='/sb/admin/:path?/:path?/:path?/:path?' exact>
        <HideChatBot />
        <Suspense
          fallback={
            <div className='loadingContainer'>
              <Spin tip='Loading...' />
            </div>
          }
        >
          <MvpAdminRouter />
        </Suspense>
      </Route>
      {is_at_events_enabled && (
        <Route path={['/event/signup']}>
          <ScrollToTop />
          <MainLayout isEventSignUp>
            <Suspense
              fallback={
                <div className='loadingContainer'>
                  <Spin tip='Loading...' />
                </div>
              }
            >
              <Switch>
                <Route
                  exact
                  path='/event/signup'
                  render={(props) => <EventSignUp {...props} />}
                />
              </Switch>
            </Suspense>
          </MainLayout>
        </Route>
      )}
      {is_at_events_enabled && (
        <Route
          path={[
            '/events/one-click',
            '/events/coming-soon',
            '/events/rlw-approval',
            '/events/event-type-approval',
            '/events/volunteer-agreement',
          ]}
        >
          <ScrollToTop />
          <Suspense
            fallback={
              <div className='loadingContainer'>
                <Spin tip='Loading...' />
              </div>
            }
          >
            <Switch>
              <Route
                exact
                path='/events/one-click'
                render={(props) => <EventOneClickRedirect {...props} />}
              />
              <Route
                exact
                path='/events/coming-soon'
                render={(props) => <EventComingSoon {...props} />}
              />
              <Route
                exact
                path='/events/rlw-approval'
                render={(props) => <RodLicenceWaiverApproval {...props} />}
              />
              <Route
                exact
                path='/events/event-type-approval'
                render={(props) => <EventTypeApproval {...props} />}
              />
              <Route
                exact
                path='/events/volunteer-agreement'
                render={(props) => <EventVolunteerAgreementForm {...props} />}
              />
            </Switch>
          </Suspense>
        </Route>
      )}
      <Route path={['/login']}>
        <ScrollToTop />
        <MainLayout>
          <Suspense
            fallback={
              <div className='loadingContainer'>
                <Spin tip='Loading...' />
              </div>
            }
          >
            <Switch>
              <Route
                exact
                path='/search'
                render={(props) => <SearchView {...props} />}
              />
              <Route
                exact
                path='/contact'
                render={(props) => <ContactUs {...props} />}
              />
              <Route
                exact
                path='/contact/anglers'
                render={(props) => <ContactUsAnglers {...props} />}
              />
              <Route
                exact
                path='/contact/fishery'
                render={(props) => <ContactUsFishery {...props} />}
              />
              <Route
                exact
                path='/articles'
                render={(props) => <Articles {...props} />}
              />
              <Route
                exact
                path='/article/:article_id'
                render={(props) => <ViewArticle {...props} />}
              />
            </Switch>
          </Suspense>
        </MainLayout>
      </Route>
      <Route path={['/search']}>
        <ScrollToTop />
        <Suspense
          fallback={
            <div className='loadingContainer'>
              <Spin tip='Loading...' />
            </div>
          }
        >
          <Route
            exact
            path='/search'
            render={(props) => <SearchView {...props} />}
          />
        </Suspense>
      </Route>
      <Route path={['/events/search']}>
        <ScrollToTop />
        <Suspense
          fallback={
            <div className='loadingContainer'>
              <Spin tip='Loading...' />
            </div>
          }
        >
          <Route
            exact
            path='/events/search'
            render={(props) => <EventSearch {...props} />}
          />
        </Suspense>
      </Route>
      <Route path={['/event/:event_id']}>
        <Suspense
          fallback={
            <div className='loadingContainer'>
              <Spin tip='Loading...' />
            </div>
          }
        >
          <Switch>
            <Route
              path='/event/:event_id/:drawer_key?'
              render={(props) => <EventProfile {...props} />}
            />
          </Switch>
        </Suspense>
      </Route>

      <Route path={['/user/signup/step2/:id']}>
        <ScrollToTop />
        <Suspense
          fallback={
            <div className='loadingContainer'>
              <Spin tip='Loading...' />
            </div>
          }
        >
          <Switch>
            <Route
              exact
              path='/user/signup/step2/:id'
              render={(props) => <EmailVerificationPage {...props} />}
            />
          </Switch>
        </Suspense>
      </Route>
      <Route path={['/swimbooker-fishery-manager']}>
        <ScrollToTop />
        <Suspense
          fallback={
            <div className='loadingContainer'>
              <Spin tip='Loading...' />
            </div>
          }
        >
          <Switch>
            <Route
              exact
              path='/swimbooker-fishery-manager'
              render={(props) => <FisheryPromotion {...props} />}
            />
          </Switch>
        </Suspense>
      </Route>
      <Route path={['/fishery/10972/angler-details-form']}>
        <ScrollToTop />

        <Switch>
          <Route
            exact
            path='/fishery/10972/angler-details-form'
            render={(props) => <SandhurstAnglerDetails {...props} />}
          />
        </Switch>
      </Route>
      <Route path={['/about']}>
        <ScrollToTop />
        <Suspense
          fallback={
            <div className='loadingContainer'>
              <Spin tip='Loading...' />
            </div>
          }
        >
          <Switch>
            <Route exact path='/about' component={AboutUs} />
          </Switch>
        </Suspense>
      </Route>
      <Route path={['/user/signup']}>
        <ScrollToTop />
        <MainLayout isSignUp>
          <Suspense
            fallback={
              <div className='loadingContainer'>
                <Spin tip='Loading...' />
              </div>
            }
          >
            <Switch>
              <Route
                exact
                path='/user/signup'
                render={(props) => <Signup {...props} />}
              />
            </Switch>
          </Suspense>
        </MainLayout>
      </Route>
      <Route path={['/member/new_user', '/member/accept_waitlist']}>
        <Suspense
          fallback={
            <div className='loadingContainer'>
              <Spin tip='Loading...' />
            </div>
          }
        >
          <MemberSignUpLayout>
            <Switch>
              <Route
                exact={false}
                path={'/member/new_user'}
                render={(props) => <MemberAccountCreation {...props} />}
              />
              <Route
                exact={false}
                path={'/member/accept_waitlist'}
                render={(props) => <MemberWaitListSignUp {...props} />}
              />
            </Switch>
          </MemberSignUpLayout>
        </Suspense>
      </Route>
      <Route
        path={[
          '/booking/new/:fishery_id',
          '/booking/progress/:fishery_id',
          '/booking/new_user',
        ]}
      >
        <Suspense
          fallback={
            <div className='loadingContainer'>
              <Spin tip='Loading...' />
            </div>
          }
        >
          <Switch>
            <Route
              exact={false}
              path={'/booking/new_user'}
              render={(props) => (
                <div className={'user_booking_outer_container'}>
                  <div className={'user_booking_container--max-width bg-white'}>
                    <UserBookingAccountCreationPage {...props} />
                  </div>
                </div>
              )}
            />
            <Route
              exact
              path={[
                '/booking/new/:fishery_id',
                '/booking/progress/:fishery_id',
              ]}
              render={(props) => <UserBookingsPage {...props} />}
            />
          </Switch>
        </Suspense>
      </Route>
      <Route
        path={[
          '/booking/view_packages/:fishery_id',
          '/booking/package_details/:id',
          '/booking/package/:fishery_id',
          '/booking/packages/new/:fishery_id',
        ]}
      >
        <Suspense
          fallback={
            <div className='loadingContainer'>
              <Spin tip='Loading...' />
            </div>
          }
        >
          <Switch>
            <Route
              exact
              path={['/booking/view_packages/:fishery_id']}
              render={(props) => (
                <div>
                  <ExoticHeader />
                  <AllBookingPackage {...props} />
                </div>
              )}
            />
            <Route
              exact
              path={['/booking/package/:fishery_id']}
              render={(props) => (
                <div>
                  <ExoticHeader />
                  <PackageOption {...props} />
                </div>
              )}
            />
            <Route
              exact
              path={['/booking/package_details/:id']}
              render={(props) => (
                <div>
                  <ExoticHeader />
                  <PackageDetails {...props} />
                </div>
              )}
            />
          </Switch>
        </Suspense>
      </Route>
      <Route path={'/payment'}>
        <Suspense
          fallback={
            <div className='loadingContainer'>
              <Spin tip='Loading...' />
            </div>
          }
        >
          <Switch>
            <Route
              exact={false}
              path={'/payment'}
              render={(props) => <UserAsyncPaymentPage {...props} />}
            />
          </Switch>
        </Suspense>
      </Route>
      <Route path={['/fishery/:fishery_id']}>
        <Suspense
          fallback={
            <div className='loadingContainer'>
              <Spin tip='Loading...' />
            </div>
          }
        >
          <Switch>
            <Route
              path='/fishery/:fishery_id/:drawer_key?'
              render={(props) => <FisheryProfile {...props} />}
            />
          </Switch>
        </Suspense>
      </Route>
      <Route path={['/user/email_preferences', '/app']}>
        <ScrollToTop />
        <Suspense
          fallback={
            <div className='loadingContainer'>
              <Spin tip='Loading...' />
            </div>
          }
        >
          <Switch>
            <Route
              exact
              path='/user/email_preferences'
              render={(props) => <UserEmailManagement {...props} />}
            />
            <Route exact path='/app' component={AppDownloadLinks} />
          </Switch>
        </Suspense>
      </Route>
      <Route path={['/user/profile']}>
        <ScrollToTop />
        <Suspense
          fallback={
            <div className='loadingContainer'>
              <Spin tip='Loading...' />
            </div>
          }
        >
          <Switch>
            <Route
              exact
              path='/user/profile/:section_key?'
              render={(props) => <UserProfile {...props} />}
            />
          </Switch>
        </Suspense>
      </Route>
      <Route path={['/about']}>
        <ScrollToTop />
        <Suspense
          fallback={
            <div className='loadingContainer'>
              <Spin tip='Loading...' />
            </div>
          }
        >
          <Switch>
            <Route exact path='/about' component={AboutUs} />
          </Switch>
        </Suspense>
      </Route>
      <Route path={['/brands']}>
        <ScrollToTop />
        <HomeLayout isArticlePage>
          <Suspense
            fallback={
              <div className='loadingContainer'>
                <Spin tip='Loading...' />
              </div>
            }
          >
            <Switch>
              <Route
                exact
                path='/brands'
                render={(props) => <Brands {...props} />}
              />
            </Switch>
          </Suspense>
        </HomeLayout>
      </Route>
      <Route path={['/articles', '/article/:article_id']}>
        <ScrollToTop />
        <HomeLayout isArticlePage>
          <Suspense
            fallback={
              <div className='loadingContainer'>
                <Spin tip='Loading...' />
              </div>
            }
          >
            <Switch>
              <Route
                exact
                path='/articles'
                render={(props) => <Articles {...props} />}
              />
              <Route
                exact
                path='/article/:article_id'
                render={(props) => <Articles {...props} />}
              />
            </Switch>
          </Suspense>
        </HomeLayout>
      </Route>
      <Route path={['/contact']}>
        <ScrollToTop />
        <HomeLayout isArticlePage>
          <Suspense
            fallback={
              <div className='loadingContainer'>
                <Spin tip='Loading...' />
              </div>
            }
          >
            <Switch>
              <Route
                exact
                path='/contact'
                render={(props) => <ContactUs {...props} />}
              />
            </Switch>
          </Suspense>
        </HomeLayout>
      </Route>
      <Route path={['/home', '/', '/login']} exact>
        <ScrollToTop />
        <HomeLayout>
          <Suspense
            fallback={
              <div className='loadingContainer'>
                <Spin tip='Loading...' />
              </div>
            }
          >
            <Switch>
              <Route exact path='/' component={Home} />

              <Route exact path='/home' component={Home} />

              <Route exact path='/login' component={Login} />
            </Switch>
          </Suspense>
        </HomeLayout>
      </Route>

      <Route path={['/404']}>
        <MainLayout>
          <ErrorPage />
        </MainLayout>
      </Route>
      <Route>
        <MainLayout>
          <ErrorPage />
        </MainLayout>
      </Route>
    </Switch>
  );
};

export default BaseRouter;
